import React, { useState, useEffect } from 'react';
import { getTodayYYYYMMDD, formatDateToDDMMYYYY } from '../../utils/dateUtils';
import './BalanceEnquiry.css';

const BalanceEnquiry = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Fetch data on component mount (default: all data)
    fetchBalanceData();
  }, []);

  const fetchBalanceData = async () => {
    setIsLoading(true);
    setError('');
    try {
      let url = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5003'}/balance-enquiry`;
      
      // Add date filters if provided
      if (dateFrom && dateTo) {
        url += `?date_from=${dateFrom}&date_to=${dateTo}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setBalanceData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch balance data.');
      }
    } catch (err) {
      console.error('Error fetching balance data:', err);
      setError('Error occurred while fetching balance data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyFilter = () => {
    // Validate dates if both are provided
    if (dateFrom && dateTo) {
      if (new Date(dateFrom) > new Date(dateTo)) {
        alert('From date cannot be greater than To date.');
        return;
      }
    }
    fetchBalanceData();
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    // Fetch all data
    setTimeout(() => {
      fetchBalanceData();
    }, 100);
  };

  const formatCurrency = (amount) => {
    const value = Number(amount || 0);
    return `₹ ${value.toFixed(2)}`;
  };

  const getRowClassName = (value) => {
    if (value < 0) return 'negative-value';
    if (value > 0) return 'positive-value';
    return '';
  };

  const getTotalRowClassName = (value) => {
    if (value < 0) return 'negative-value total-row';
    if (value > 0) return 'positive-value total-row';
    return 'total-row';
  };

  return (
    <div className="balance-enquiry-container">
      <h2>Balance Enquiry</h2>

      {/* Date Filter Section */}
      <div className="filter-section">
        <h3>Date Duration Filter</h3>
        <div className="date-filters">
          <div className="filter-group">
            <label>From Date:</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-group">
            <label>To Date:</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="filter-input"
            />
          </div>
          <div className="filter-buttons">
            <button onClick={handleApplyFilter} className="apply-button">
              Apply Filter
            </button>
            <button onClick={handleClearFilter} className="clear-button">
              Clear Filter
            </button>
          </div>
        </div>
        <p className="filter-note">
          {dateFrom && dateTo 
            ? `Showing data from ${formatDateToDDMMYYYY(dateFrom)} to ${formatDateToDDMMYYYY(dateTo)}`
            : 'Showing all data (no date filter applied)'}
        </p>
      </div>

      {/* Balance Table */}
      {isLoading ? (
        <div className="loading-message">Loading balance data...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : balanceData ? (
        <div className="balance-table-section">
          <h3>Balance Summary</h3>
          <table className="balance-table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>General Loan (Debit)</td>
                <td className="right">{formatCurrency(balanceData.generalLoanDebit)}</td>
              </tr>
              <tr>
                <td>CD Loan (Debit)</td>
                <td className="right">{formatCurrency(balanceData.cdLoanDebit)}</td>
              </tr>
              <tr>
                <td>TBD Loan (Debit)</td>
                <td className="right">{formatCurrency(balanceData.tbdLoanDebit)}</td>
              </tr>
              <tr>
                <td>GeneralLoan Interest (Credit)</td>
                <td className="right">{formatCurrency(balanceData.generalLoanInterest)}</td>
              </tr>
              <tr>
                <td>CD Loan Interest (Credit)</td>
                <td className="right">{formatCurrency(balanceData.cdLoanInterest)}</td>
              </tr>
              <tr>
                <td>TBD Loan Interest (Credit)</td>
                <td className="right">{formatCurrency(balanceData.tbdLoanInterest)}</td>
              </tr>
              <tr>
                <td>GeneralLoan EMI (Credit)</td>
                <td className="right">{formatCurrency(balanceData.generalLoanEMI)}</td>
              </tr>
              <tr>
                <td>CD Loan EMI (Credit)</td>
                <td className="right">{formatCurrency(balanceData.cdLoanEMI)}</td>
              </tr>
              <tr>
                <td>TBD Loan EMI (Credit)</td>
                <td className="right">{formatCurrency(balanceData.tbdLoanEMI)}</td>
              </tr>
              <tr>
                <td>Expense (Debit)</td>
                <td className="right">{formatCurrency(balanceData.expenseDebit)}</td>
              </tr>
              <tr>
                <td>Lucky Draw (Debit)</td>
                <td className="right">{formatCurrency(balanceData.luckyDrawDebit)}</td>
              </tr>
              <tr>
                <td>Document charges (Credit)</td>
                <td className="right">{formatCurrency(balanceData.documentChargesCredit)}</td>
              </tr>
              <tr>
                <td>Total amount collected from the FD (Credit)</td>
                <td className="right">{formatCurrency(balanceData.fdTotalAmount)}</td>
              </tr>
              <tr className={getTotalRowClassName(balanceData.totalCredit)}>
                <td><strong>Total Credit</strong></td>
                <td className="right"><strong>{formatCurrency(balanceData.totalCredit)}</strong></td>
              </tr>
              <tr className={getTotalRowClassName(balanceData.totalDebit)}>
                <td><strong>Total Debit</strong></td>
                <td className="right"><strong>{formatCurrency(balanceData.totalDebit)}</strong></td>
              </tr>
              <tr className={getTotalRowClassName(balanceData.overallBalance)}>
                <td><strong>Overall Balance</strong></td>
                <td className="right"><strong>{formatCurrency(balanceData.overallBalance)}</strong></td>
              </tr>
              <tr className={getTotalRowClassName(balanceData.fixedBalance)}>
                <td><strong>Fixed Balance</strong></td>
                <td className="right"><strong>{formatCurrency(balanceData.fixedBalance)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-data-message">No balance data available</div>
      )}
    </div>
  );
};

export default BalanceEnquiry;

